import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isValid, parse } from 'date-fns';
import { es as esLocale } from 'date-fns/locale';
import type { UpdateCustomerProfileDto } from '../../../api/models/UpdateCustomerProfileDto';
import { ApiError } from '../../../api/core/ApiError';
import { parseCustomerApiErrorMessage } from '../../../utils/customerApiMessages';
import { salesService } from '../../../services/sales.service';
import type { CustomerSalesHistoryItem } from '../../../types/sales.types';
import {
  CLIENT_DRAWER_EMAIL_REGEX,
  CLIENT_DRAWER_HISTORY_PAGE_SIZE,
  type ClientCreateDrawerProps,
  type ClientDrawerFormState,
  type ClientDrawerStep,
} from './clientCreateDrawer.types';
import { splitPhoneValue, validatePhoneByCountry } from '../../../utils/phone';
import {
  buildCreateCustomerDto,
  emptyClientDrawerForm,
  normalizeClientGender,
  normalizeClientPaymentMethod,
  parseClientDrawerApiErrorMessage,
} from './clientCreateDrawer.utils';
import { saveCrmClientDrawerRestore } from './crmClientDrawerRestore';

export function useClientCreateDrawer({
  open,
  onClose,
  mode = 'create',
  initialData,
  onCreated,
  onUpdated,
}: ClientCreateDrawerProps) {
  const navigate = useNavigate();
  const isEditMode = mode === 'edit';
  const isViewMode = mode === 'view';

  const [form, setForm] = useState<ClientDrawerFormState>(emptyClientDrawerForm);
  const [currentStep, setCurrentStep] = useState<ClientDrawerStep>('FORM');
  const [initialSnapshot, setInitialSnapshot] = useState<string>('');
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [identityDocumentError, setIdentityDocumentError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [historyItems, setHistoryItems] = useState<CustomerSalesHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);

  useEffect(() => {
    if (open) {
      const normalizedDate =
        initialData?.dateOfBirth instanceof Date
          ? initialData.dateOfBirth.toISOString().slice(0, 10)
          : (initialData?.dateOfBirth ?? '');
      const next = {
        ...emptyClientDrawerForm(),
        ...initialData,
        dateOfBirth: normalizedDate,
      } as ClientDrawerFormState;
      setForm(next);
      setCurrentStep(isViewMode ? 'CONFIRMATION' : 'FORM');
      setInitialSnapshot(JSON.stringify(next));
      setError(null);
      setNameError(null);
      setEmailError(null);
      setIdentityDocumentError(null);
      setPhoneError(null);
      setShowCancelConfirmation(false);
      setShowSaveConfirmModal(false);
    }
  }, [open, initialData, isViewMode]);

  const loadSalesHistory = useCallback(
    async (page: number = 1) => {
      if (!isViewMode || initialData?.id == null) return;
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const response = await salesService.getCustomerSalesHistory(initialData.id, {
          page,
          limit: CLIENT_DRAWER_HISTORY_PAGE_SIZE,
        });
        setHistoryItems(response.data);
        setHistoryPage(response.page);
        setHistoryTotal(response.total);
      } catch (e) {
        setHistoryError(
          e instanceof Error ? e.message : 'No se pudo cargar el historial de ventas del cliente.',
        );
      } finally {
        setHistoryLoading(false);
      }
    },
    [initialData?.id, isViewMode],
  );

  useEffect(() => {
    if (open && isViewMode && initialData?.id != null) {
      void loadSalesHistory(1);
    }
  }, [open, isViewMode, initialData?.id, loadSalesHistory]);

  const hasDirty = useMemo(() => JSON.stringify(form) !== initialSnapshot, [form, initialSnapshot]);

  const handleCloseClick = useCallback(() => {
    if (isViewMode) {
      onClose();
      return;
    }
    if (hasDirty && !isSubmitting) {
      setShowCancelConfirmation(true);
    } else {
      onClose();
    }
  }, [hasDirty, isSubmitting, isViewMode, onClose]);

  const cancelOperation = useCallback(() => {
    setShowCancelConfirmation(false);
    onClose();
  }, [onClose]);

  const validateStepOne = useCallback(() => {
    if (isViewMode) return true;
    const trimmedName = form.customerName.trim();
    const trimmedEmail = form.customerEmail.trim();
    const trimmedCi = form.identityDocumentNumber.trim();
    let hasError = false;

    if (!trimmedName) {
      setNameError('El nombre es obligatorio');
      hasError = true;
    } else {
      setNameError(null);
    }

    if (trimmedCi.length > 32) {
      setIdentityDocumentError('Máximo 32 caracteres.');
      hasError = true;
    } else {
      setIdentityDocumentError(null);
    }

    if (trimmedEmail !== '' && !CLIENT_DRAWER_EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError('Ingresa un correo válido');
      hasError = true;
    } else {
      setEmailError(null);
    }

    if (form.customerPhone.trim()) {
      const { countryIso, nationalNumber } = splitPhoneValue(form.customerPhone);
      const telErr = validatePhoneByCountry(countryIso, nationalNumber);
      if (telErr) {
        setPhoneError(telErr);
        hasError = true;
      } else {
        setPhoneError(null);
      }
    } else {
      setPhoneError(null);
    }

    return !hasError;
  }, [
    form.customerEmail,
    form.customerName,
    form.customerPhone,
    form.identityDocumentNumber,
    isViewMode,
  ]);

  const handleNextStep = useCallback(() => {
    if (isViewMode) return;
    setError(null);
    if (!validateStepOne()) return;
    setCurrentStep('CONFIRMATION');
  }, [isViewMode, validateStepOne]);

  const handlePreviousStep = useCallback(() => setCurrentStep('FORM'), []);

  const handleOpenSaveConfirmation = useCallback(() => {
    if (isViewMode) return;
    setError(null);
    if (!validateStepOne()) return;
    setShowSaveConfirmModal(true);
  }, [isViewMode, validateStepOne]);

  const handleCancelSaveConfirmation = useCallback(() => {
    setShowSaveConfirmModal(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (isViewMode) return;
    setError(null);
    if (!validateStepOne()) {
      setShowSaveConfirmModal(false);
      return;
    }
    setIsSubmitting(true);
    try {
      if (isEditMode) {
        if (initialData?.id == null) {
          setError('No se pudo identificar al cliente.');
          setShowSaveConfirmModal(false);
          return;
        }
        const dto = buildCreateCustomerDto(form);
        const patch: UpdateCustomerProfileDto = {
          customerName: dto.customerName,
          customerEmail: dto.customerEmail,
          customerPhone: dto.customerPhone,
          referenceNumber1: form.referenceNumber1.trim() || undefined,
          referenceNumber2: form.referenceNumber2.trim() || undefined,
          customerAddress: dto.customerAddress,
          dateOfBirth: dto.dateOfBirth,
          gender: dto.gender,
          preferredPaymentMethod: dto.preferredPaymentMethod,
          customerNotes: dto.customerNotes,
          loyaltyTier: dto.loyaltyTier,
          marketingConsent: dto.marketingConsent,
          referralSource: dto.referralSource,
          identityDocumentNumber: dto.identityDocumentNumber ?? undefined,
        };
        await salesService.updateCustomer(initialData.id, patch);
        onUpdated?.({
          id: initialData.id,
          ...form,
        });
      } else {
        const created = await salesService.createCustomer(buildCreateCustomerDto(form));
        onCreated?.({
          id: created.id,
          customerName: created.customerName,
          identityDocumentNumber: created.identityDocumentNumber ?? '',
          customerEmail: created.customerEmail ?? '',
          customerPhone: created.customerPhone ?? '',
          customerAddress: created.customerAddress ?? '',
          dateOfBirth: created.dateOfBirth ?? '',
          gender: normalizeClientGender(created.gender),
          preferredPaymentMethod: normalizeClientPaymentMethod(created.preferredPaymentMethod),
          customerNotes: created.customerNotes ?? '',
          loyaltyTier: created.loyaltyTier as ClientDrawerFormState['loyaltyTier'],
          marketingConsent: created.marketingConsent,
          referralSource: created.referralSource ?? '',
        });
      }
      setShowSaveConfirmModal(false);
      onClose();
    } catch (e) {
      const fallback = isEditMode
        ? 'No se pudo guardar los cambios.'
        : 'No se pudo crear el cliente.';
      const rawForCi =
        e instanceof ApiError
          ? parseClientDrawerApiErrorMessage(e.body) || e.message || ''
          : e instanceof Error
            ? e.message
            : '';
      const msg = parseCustomerApiErrorMessage(e) || fallback;
      setError(msg);
      if (
        e instanceof ApiError &&
        e.status === 400 &&
        /documento de identidad|ya existe un cliente con/i.test(rawForCi)
      ) {
        setIdentityDocumentError(msg);
        setCurrentStep('FORM');
      }
      setShowSaveConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    form,
    initialData?.id,
    isEditMode,
    isViewMode,
    onClose,
    onCreated,
    onUpdated,
    validateStepOne,
  ]);

  const dateOfBirthAsDate = useMemo(() => {
    if (!form.dateOfBirth?.trim()) return null;
    const d = parse(form.dateOfBirth, 'yyyy-MM-dd', new Date());
    return isValid(d) ? d : null;
  }, [form.dateOfBirth]);

  const dateOfBirthDisplay = useMemo(() => {
    if (!dateOfBirthAsDate) return form.dateOfBirth?.trim() || '-';
    return format(dateOfBirthAsDate, 'dd/MM/yyyy', { locale: esLocale });
  }, [dateOfBirthAsDate, form.dateOfBirth]);

  const minBirthDate = useMemo(() => new Date(1900, 0, 1), []);
  const maxBirthDate = useMemo(() => new Date(), []);
  const referenceBirthDate = useMemo(() => new Date(1990, 0, 15), []);

  const handleGoToOrder = useCallback(
    (orderId: number) => {
      if (isViewMode && initialData?.id != null) {
        saveCrmClientDrawerRestore(initialData.id);
      }
      navigate('/dashboard/orders', {
        state: { openOrderDetails: orderId },
      });
    },
    [initialData?.id, isViewMode, navigate],
  );

  const historyTotalPages = Math.max(1, Math.ceil(historyTotal / CLIENT_DRAWER_HISTORY_PAGE_SIZE));

  return {
    form,
    setForm,
    currentStep,
    showCancelConfirmation,
    setShowCancelConfirmation,
    showSaveConfirmModal,
    isSubmitting,
    error,
    setError,
    nameError,
    emailError,
    identityDocumentError,
    phoneError,
    historyItems,
    historyLoading,
    historyError,
    historyPage,
    historyTotal,
    historyTotalPages,
    loadSalesHistory,
    hasDirty,
    handleCloseClick,
    cancelOperation,
    validateStepOne,
    handleNextStep,
    handlePreviousStep,
    handleOpenSaveConfirmation,
    handleCancelSaveConfirmation,
    handleSubmit,
    dateOfBirthAsDate,
    dateOfBirthDisplay,
    minBirthDate,
    maxBirthDate,
    referenceBirthDate,
    handleGoToOrder,
    isEditMode,
    isViewMode,
  };
}
